import { Alert, Button, Card, Empty, Table } from 'antd';
import type { TablePaginationConfig, TableProps } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import React, { useCallback, useMemo } from 'react';

import type { PortfolioProjectRow, PortfolioSortField, SortDirection } from '@/api/portfolio/api';
import { buildPortfolioTableColumns } from '@/components/pages/portfolio/portfolioTableColumns';

const PAGE_SIZE_OPTIONS = ['10', '25', '50'];

/**
 * Column width sum: 44 + 140 + 352 + 90 + 84 + 96 + 96 + 80 + 80 + 140 + 120 +
 * 150 + 120 + 210 + 80 + 80 + 60 = 1896px. Used as `scroll.x` so the table has
 * a definite pixel width; combined with `tableLayout="fixed"`, columns honor
 * their widths exactly. Health columns are always included.
 */
const TABLE_MIN_WIDTH = 1896;
const TABLE_EMPTY = { emptyText: <Empty description="No projects match your current filters." /> };

export interface PortfolioTableProps {
  rows: PortfolioProjectRow[];
  total: number;
  page: number;
  perPage: number;
  loading: boolean;
  error: string | null;
  sortBy: PortfolioSortField | null;
  sortDir: SortDirection;
  onChangePage: (page: number, perPage: number) => void;
  onChangeSort: (sortBy: PortfolioSortField | null, sortDir: SortDirection) => void;
  onRetry: () => void;
  onRowClick?: (row: PortfolioProjectRow, event?: React.MouseEvent<HTMLElement>) => void;
  onClientClick: (clientId: string) => void;
  onAddSharePointLink: (row: PortfolioProjectRow) => void;
}

/** Card-wrapped portfolio project table with server-driven sort + pagination. */
const PortfolioTableComponent: React.FC<PortfolioTableProps> = ({
  rows,
  total,
  page,
  perPage,
  loading,
  error,
  sortBy,
  sortDir,
  onChangePage,
  onChangeSort,
  onRetry,
  onRowClick,
  onClientClick,
  onAddSharePointLink,
}) => {
  const tableScroll = useMemo(() => ({ x: TABLE_MIN_WIDTH }), []);

  const columns = useMemo(
    () =>
      buildPortfolioTableColumns({
        sortBy,
        sortDir,
        onClientClick,
        onAddSharePointLink,
      }),
    [sortBy, sortDir, onClientClick, onAddSharePointLink],
  );

  const rowClassName = useCallback(
    (row: PortfolioProjectRow) =>
      [
        onRowClick ? 'pf-table__row--clickable' : '',
        row.missing_update_flag ? 'pf-table__row--missing-update' : '',
      ]
        .filter(Boolean)
        .join(' '),
    [onRowClick],
  );
  const onRow = useCallback(
    (row: PortfolioProjectRow) => ({
      onClick: (event: React.MouseEvent<HTMLElement>) => onRowClick?.(row, event),
      onAuxClick: (event: React.MouseEvent<HTMLElement>) => {
        // Middle-click on a row opens the project in a new tab.
        if (event.button === 1) {
          onRowClick?.(row, event);
        }
      },
    }),
    [onRowClick],
  );
  const pagination = useMemo<TablePaginationConfig>(
    () => ({
      current: page,
      pageSize: perPage,
      total,
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      showTotal: (count, range) => `Showing ${range[0]}–${range[1]} of ${count} projects`,
    }),
    [page, perPage, total],
  );

  const handleChange = useCallback<NonNullable<TableProps<PortfolioProjectRow>['onChange']>>(
    (pagination: TablePaginationConfig, _filters, sorter) => {
      const single = (
        Array.isArray(sorter) ? sorter[0] : sorter
      ) as SorterResult<PortfolioProjectRow>;
      const nextSortBy = single?.order ? (single.columnKey as PortfolioSortField) : null;
      const nextSortDir: SortDirection = single?.order === 'descend' ? 'desc' : 'asc';

      const sortChanged = nextSortBy !== sortBy || (nextSortBy !== null && nextSortDir !== sortDir);
      if (sortChanged) {
        onChangeSort(nextSortBy, nextSortDir);
        return;
      }
      onChangePage(pagination.current ?? 1, pagination.pageSize ?? perPage);
    },
    [sortBy, sortDir, perPage, onChangeSort, onChangePage],
  );

  return (
    <Card className="pf-table-card">
      {error ? (
        <Alert
          className="pf-table-card__error"
          type="error"
          showIcon
          message="Unable to load portfolio"
          description={error}
          action={
            <Button size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        />
      ) : null}

      <Table<PortfolioProjectRow>
        rowKey="id"
        className="pf-table"
        tableLayout="fixed"
        loading={loading}
        columns={columns}
        dataSource={rows}
        onChange={handleChange}
        rowClassName={rowClassName}
        onRow={onRow}
        scroll={tableScroll}
        pagination={pagination}
        locale={TABLE_EMPTY}
      />
    </Card>
  );
};

/**
 * Memoized so filter/search state changes elsewhere on the page don't re-render
 * the table; it only re-renders when its own data/sort/pagination props change.
 */
export const PortfolioTable = React.memo(PortfolioTableComponent);

export default PortfolioTable;
